import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getRequisitionsReports } from '../../../models/requisitionModel';
import { getSuppliers, getWorkflows } from '../../../models/specificationModel';
import { getSettings } from '../../../models/settingsModel';
import { toast } from 'react-toastify'; 
import api from '../../../services/api';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import SearchableSelect from '../../../components/common/SearchableSelect';

function RequisitionsReports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  
  // Check if user is HOD
  const isHOD = user && (user.hod == 1 || user.hod === '1');
  
  // Dropdown data
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [glCodes, setGlCodes] = useState([]);
  const [capexValues, setCapexValues] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ site_limit: 20000, site_limit_above: '1' });
  
  // Debounce timer for search input
  const searchDebounceTimer = useRef(null);
  
  // Get current month's start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format as dd/MM/yyyy to match CustomDatePicker format
    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay)
    };
  };
  
  const [filters, setFilters] = useState({
    status: 'pending', // Default to pending
    department: '',
    requester: '',
    supplier: '',
    glCode: '',
    capex: '', // Specific capex number or ''
    emergencyStatus: '', // 'emergency', 'non-emergency', or ''
    emergency: '', // For backward compatibility
    dateFrom: '',
    dateTo: '',
    dateRange: getCurrentMonthDates(), // Default to current month
    amountMin: '',
    amountMax: '',
    amountRange: {
      min: '',
      max: ''
    },
    search: '' // Search specification title
  });

  // Load initial data on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettings();
        if (response.status === 1 && response.data) {
          setGlobalSettings(response.data);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
    loadDropdownData();
    fetchRequisitionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Re-fetch requisitions when non-search filters change (immediate)
  useEffect(() => {
    if (departments.length > 0) { // Only fetch after dropdowns are loaded
      fetchRequisitionsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status,
    filters.department,
    filters.requester,
    filters.supplier,
    filters.glCode,
    filters.capex,
    filters.emergency,
    filters.dateRange,
    filters.amountRange
  ]); // Trigger on non-search filter changes
  
  // Debounced search effect - triggers after 500ms of no typing
  useEffect(() => {
    if (departments.length > 0) {
      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
      
      // Set new timer for search
      searchDebounceTimer.current = setTimeout(() => {
        fetchRequisitionsData();
      }, 500); // Wait 500ms after user stops typing
      
      // Cleanup function
      return () => {
        if (searchDebounceTimer.current) {
          clearTimeout(searchDebounceTimer.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]); // Only trigger on search changes

  const loadDropdownData = async () => {
    try {
      // Load all data in parallel including requisitions for capex extraction
      const [usersFormData, allUsersData, suppliersData, workflowsData, requisitionsForCapex] = await Promise.all([
        api.get('/api/users/create'), // For departments
        api.get('/api/users?pageSize=5000&limit=5000'), // Get all users with large page size
        getSuppliers(),
        getWorkflows(),
        getRequisitionsReports() // Use dedicated reporting endpoint for capex extraction
      ]);

      // Declare deptList at function scope so it's available for user filtering
      let deptList = [];

      // Process departments from create form endpoint
      if (usersFormData?.data) {
        if (usersFormData.data.status === 1 && usersFormData.data.data) {
          deptList = usersFormData.data.data.departments || [];
          console.log('📊 Departments loaded from API:', deptList.length);
        }
        
        // Filter departments for HOD users based on their division's parent department
        // Check if HOD has a valid department_id (user.department_id field, NOT user.department)
   
        
        const hasValidDepartmentId = user?.department_id && 
                                     user.department_id !== 0 && 
                                     user.department_id !== '0' && 
                                     user.department_id !== '' &&
                                     user.department_id !== null &&
                                     user.department_id !== undefined;
         
        
        if (isHOD && hasValidDepartmentId) {
          // For HOD users with valid department_id: Filter by their department_id directly
          const userDepartmentId = user.department_id;
           
          
          // Filter divisions to show only those that belong to the HOD's department
          const originalCount = deptList.length;
          
          deptList = deptList.filter(division => {
            const divisionDeptStr = String(division.division_department);
            const userDeptStr = String(userDepartmentId); 
            return divisionDeptStr === userDeptStr;
          });
           
        } else if (isHOD && (!user?.department_id || user.department_id === 0 || user.department_id === '0')) {
          // HOD user but no valid department_id - don't filter, show all divisions
        }
        
        setDepartments(deptList);
      }

      // Process users from all users endpoint
      if (allUsersData?.data) {
        let usersList = [];
        
        // Handle different response structures
        if (allUsersData.data.status === 1) {
          // Handle paginated response: { data: { status: 1, data: { rows: [...] } } }
          if (allUsersData.data.data && Array.isArray(allUsersData.data.data.rows)) {
            usersList = allUsersData.data.data.rows;
          }
          // Handle direct data response: { data: { status: 1, data: [...] } }
          else if (allUsersData.data.data && Array.isArray(allUsersData.data.data)) {
            usersList = allUsersData.data.data;
          }
          // Handle { data: { status: 1, users: [...] } }
          else if (allUsersData.data.users && Array.isArray(allUsersData.data.users)) {
            usersList = allUsersData.data.users;
          }
        } 
        // Handle direct array response { data: [...] }
        else if (Array.isArray(allUsersData.data)) {
          usersList = allUsersData.data;
        }
        
        // Filter users based on HOD status and department hierarchy
        if (isHOD) {
          // For HOD users: Show users from divisions associated with their department
          // Only filter if HOD has valid department_id
          if (user?.department_id && user.department_id !== 0 && user.department_id !== '0') {
            const hodDepartmentId = String(user.department_id);
            
            // Filter users: show only users whose division belongs to the HOD's department
            usersList = usersList.filter(u => {
              const uDivisionId = String(u.user_department || u.department);
              
              // Find this user's division in deptList
              const uDivision = deptList.find(d => String(d.department_id) === uDivisionId);
              
              // Check if this user's division belongs to the HOD's department
              if (uDivision && uDivision.division_department) {
                const uDepartmentId = String(uDivision.division_department);
                return uDepartmentId === hodDepartmentId;
              }
              
              return false; // If division not found, exclude user
            });
          } else {
            // HOD user but no valid department_id - show all users
          }
        } else if (!isHOD && user?.department) {
          // For non-HOD users: Show only users from their own division
          const userDivisionId = String(user.department);
          
          usersList = usersList.filter(u => {
            const uDivisionId = String(u.user_department || u.department);
            return uDivisionId === userDivisionId;
          });
        }
        
        // Ensure we set an array, even if empty
        setUsers(Array.isArray(usersList) ? usersList : []);
      } else {
        setUsers([]); // Set empty array as fallback
      }

      // Extract unique capex values from requisitions using dedicated reporting endpoint
      if (requisitionsForCapex?.status === 1 && requisitionsForCapex?.data?.rows) {
        const allRequisitionsForCapex = requisitionsForCapex.data.rows;
        
        const uniqueCapex = [...new Set(
          allRequisitionsForCapex
            .map(req => req.requisition_capex)
            .filter(capex => capex && String(capex).trim() !== '')
        )].sort();
        setCapexValues(uniqueCapex);
      } else {
        setCapexValues([]);
      }

      // Process suppliers - already returns array directly from model
      if (suppliersData) {
        const supplierList = Array.isArray(suppliersData) ? suppliersData : [];
        setSuppliers(supplierList);
      }
      
      // Process GL Codes from workflows
      if (workflowsData) {
        const workflowList = Array.isArray(workflowsData) ? workflowsData : [];
        setGlCodes(workflowList);
      }
      
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      toast.error('Failed to load filter options');
    }
  };

  const fetchRequisitionsData = async () => {
    try {
      setLoading(true);
      
      // Use the dedicated reporting endpoint that returns all requisitions without pagination limits
      const searchParam = filters.search || filters.requester || '';
      
      // Pass status filter to backend for efficient server-side filtering
      const params = { search: searchParam };
      
      // Add status parameter if not 'all'
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      
      // Add emergency parameter if set (ESR = 1, non-ESR = 0)
      if (filters.emergency !== '') {
        params.emergency = filters.emergency;
      }
      
      const response = await getRequisitionsReports(params);
      
      if (response?.status === 1 && response?.data) {
        const allRequisitions = response.data.rows || [];
        
        // Apply client-side filters (for filters not handled by backend)
        const filteredData = applyClientFilters(allRequisitions);
        
        setRequisitions(filteredData);
        setTotalCount(filteredData.length);
        setTotalPages(1);
        
      } else {
        setRequisitions([]);
        setTotalCount(0);
      }
      
    } catch (error) {
      console.error('Error fetching requisitions reports:', error);
      toast.error('Failed to load requisitions data');
      setRequisitions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters for detailed filtering
  const applyClientFilters = (data) => {
    return data.filter(req => {
      // Status filter is now handled by backend for efficiency
      // No need to filter again on client side

      // Division (Department) filter - filter by requester's division
      if (isHOD && filters.department) {
        // For HOD users with division filter selected
        // Check if the requester's department matches the selected division
        if (req.department_name !== filters.department) {
          return false;
        }
      }
      
      // For non-HOD users, automatically filter by their own division only
      if (!isHOD && user?.department) {
        // user.department is the user's division ID
        const userDivisionId = String(user.department);
        
        // Find the user's division name
        const userDivision = departments.find(d => String(d.department_id) === userDivisionId);
        
        if (userDivision) {
          // Only show requisitions from the user's own division
          const reqDivisionName = req.department_name;
          const userDivisionName = userDivision.department_name;
          
          if (reqDivisionName !== userDivisionName) {
            return false;
          }
        }
      }

      // Requester filter (already handled by search param)
      
      // Supplier filter
      if (filters.supplier && !checkSupplierMatch(req, filters.supplier)) {
        return false;
      }

      // GL Code filter
      if (filters.glCode) {
        // Check both gl_code and gl_name fields
        if (req.gl_code !== filters.glCode && req.gl_name !== filters.glCode) {
          return false;
        }
      }

      // Capex filter (exact match for specific capex number)
      if (filters.capex && filters.capex.trim() !== '') {
        const reqCapex = req.requisition_capex ? String(req.requisition_capex).trim() : '';
        const filterCapex = String(filters.capex).trim();
        if (reqCapex !== filterCapex) {
          return false;
        }
      }

      // Exclude dummy supplier (BP_Code 11111111)
      const supplierCode = req.quotation_supplier_code || req.BP_Code || req.quotation_supplier;
      if (supplierCode === '11111111') {
        return false;
      }

      // Exclude dummy suppliers by name
      const supplierName = req.quotation_company_name || req.company_name_actual || '';
      if (supplierName.trim() === 'SILULUMANZI-DUMMY-SUPPLIER') {
        return false;
      }

      // Exclude test GL codes
      const glCode = req.gl_code || req.gl_name || '';
      if (glCode === 'TEST GL') {
        return false;
      }

      // ESR filter
      if (filters.emergency !== '' && req.specification_emergency != filters.emergency) {
        return false;
      }

      // Date range filter
      if (filters.dateRange.start) {
        // requisition_date is a Unix timestamp
        const reqDate = req.requisition_date ? new Date(req.requisition_date * 1000) : new Date(req.created_at);
        // Parse dd/MM/yyyy format from CustomDatePicker
        const startDateStr = filters.dateRange.start;
        let startDate;
        
        // Check if date is in dd/MM/yyyy format
        if (startDateStr.includes('/')) {
          const [day, month, year] = startDateStr.split('/');
          startDate = new Date(year, month - 1, day); // month is 0-indexed
        } else {
          startDate = new Date(startDateStr);
        }
        
        // Compare only dates (ignore time)
        const reqDateOnly = new Date(reqDate.getFullYear(), reqDate.getMonth(), reqDate.getDate());
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        
        if (reqDateOnly < startDateOnly) return false;
      }
      if (filters.dateRange.end) {
        // requisition_date is a Unix timestamp
        const reqDate = req.requisition_date ? new Date(req.requisition_date * 1000) : new Date(req.created_at);
        // Parse dd/MM/yyyy format from CustomDatePicker
        const endDateStr = filters.dateRange.end;
        let endDate;
        
        // Check if date is in dd/MM/yyyy format
        if (endDateStr.includes('/')) {
          const [day, month, year] = endDateStr.split('/');
          endDate = new Date(year, month - 1, day); // month is 0-indexed
        } else {
          endDate = new Date(endDateStr);
        }
        
        // Compare only dates (ignore time)
        const reqDateOnly = new Date(reqDate.getFullYear(), reqDate.getMonth(), reqDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        
        if (reqDateOnly > endDateOnly) return false;
      }

      // Amount range filter
      const quotationTotal = req.quotation_total ? 
        parseFloat(req.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
      if (filters.amountRange.min && quotationTotal < parseFloat(filters.amountRange.min)) {
        return false;
      }
      if (filters.amountRange.max && quotationTotal > parseFloat(filters.amountRange.max)) {
        return false;
      }

      return true;
    });
  };

  const checkSupplierMatch = (req, supplierCode) => {
    // Check if supplier name matches - use same logic as RequisitionsList
    const displaySupplier = getSupplierDisplay(req);
    return displaySupplier === supplierCode;
  };

  // Get supplier display name using same logic as RequisitionsList
  const getSupplierDisplay = (req) => {
    if (req.quotation_company_name && req.quotation_company_name !== 'Not Selected' && !req.quotation_company_name.startsWith('Supplier ID:')) {
      return req.quotation_company_name;
    } else if (req.company_name_actual && req.company_name_actual !== 'Not Selected' && !req.company_name_actual.startsWith('Supplier ID:')) {
      return req.company_name_actual;
    }
    return 'None';
  };

  const requiresPMApproval = (req) => {
    // Get the limit and total
    const siteLimit = globalSettings?.site_limit ? parseFloat(globalSettings.site_limit) : 20000;
    const quotationTotal = req.quotation_total ? 
      parseFloat(req.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
    
    // Apply rules
    if (quotationTotal < siteLimit) return false;
    
    return globalSettings?.site_limit_above === '1';
  };

  const getWorkflowStage = (req) => {
    // Check special states FIRST based on requisition_state field
    // requisition_state = 1 means JUNKED (not archived!)
    if (req.requisition_state == 1) {
      return 'junked';
    }
    
    // requisition_state = 7 means ARCHIVED
    if (req.requisition_state == 7) {
      return 'archived';
    }
    
    // requisition_status = 4 means REJECTED
    if (req.requisition_status === 4) {
      return 'rejected';
    }
    
    // Check if PO is issued (completion) - requisition_po_date > 0
    if (req.requisition_po_date && req.requisition_po_date > 0) {
      return 'completed';
    }
    
    const specApprovals = req.specification_approvals || 2;
    
    // CHECK APPROVAL STATUS FIRST - If fully approved, skip prerequisite checks
    if (req.requisition_status >= specApprovals) {
      // Post-approval stages - requisition is APPROVED
      if (req.requisition_assign_id && req.requisition_assign_id > 0) {
        // Buyer is assigned (check requisition_assign_id, not assigned field)
        if (req.requisition_sap_approve_time && req.requisition_sap_approve_time > 0) {
          return 'purchase_order'; // SAP draft requested, waiting for PO
        } else {
          return 'assigned'; // Assigned to buyer, no SAP draft yet
        }
      } else {
        return 'buyer_assignment'; // Waiting for buyer assignment
      }
    }
    
    // Not yet fully approved - check prerequisites and approval stages
    const hasQuote = !!(req.requisition_quote && req.requisition_quote !== '');
    
    // Check evaluation - Backend provides unserialized evaluation data
    const evalValue = req.quotation_evaluation;
    const hasEvaluation = !!(
      evalValue != null &&  // Check null/undefined first
      evalValue !== '' && 
      evalValue !== 'null' &&
      evalValue !== '0' &&
      (typeof evalValue === 'string' ? evalValue.trim().length > 0 : true)
    );
    
    const hasDeclaration = !!(req.requisition_declaration && req.requisition_declaration !== '' && req.requisition_declaration !== null);
    
    // If missing any prerequisite, it's in draft/preparation stages
    if (!hasQuote) {
      return 'quotation';
    }
    
    // Check if evaluation is missing - show status based on actual state
    // All requisitions must have evaluation before declaration
    if (!hasEvaluation) {
      return 'evaluation';
    }
    
    if (!hasDeclaration) {
      return 'declaration';
    }
    
    // All prerequisites met - NOW check if PM approval is blocking (at first approval stage only)
    const reqStatus = Number(req.requisition_status);
    const needsPMApproval = requiresPMApproval(req);
    
    
    // PM approval only blocks at first approval stage (status 0)
    if (needsPMApproval && reqStatus === 0) {
      return 'pm_approval';
    }
    
    // All prerequisites met - in approval stages
    if (reqStatus === 0) {
      return 'first_approval';
    } else if (reqStatus === 1) {
      return 'second_approval';
    } else if (reqStatus === 2) {
      if (specApprovals >= 3) {
        return 'third_approval';
      } else {
        // Status 2 but only 2 approvals needed = fully approved
        return 'buyer_assignment';
      }
    }
    
    // Fallback
    return 'draft';
  };

  const getRequisitionStatus = (req) => {
    const workflowStage = getWorkflowStage(req);
    
    // Map workflow stages to filter status categories
    switch (workflowStage) {
      case 'archived':
        return 'archived';
      case 'junked':
        return 'junked';
      case 'rejected':
        return 'rejected';
      case 'completed':
      case 'purchase_order':
      case 'assigned':
      case 'buyer_assignment':
        return 'approved'; // All post-approval stages are "approved"
      case 'pm_approval':
      case 'third_approval':
      case 'second_approval':
      case 'first_approval':
      case 'declaration':
      case 'evaluation':
      case 'quotation':
      case 'draft':
        return 'pending';
      default:
        return 'pending';
    }
  };

  const getStatusLabel = (req) => {
    const workflowStage = getWorkflowStage(req);
    
    // Return proper status labels based on workflow stage
    switch (workflowStage) {
      case 'archived':
        return 'Archived';
      case 'junked':
        return 'Junked';
      case 'rejected':
        return 'Rejected';
      case 'completed':
      case 'purchase_order':
      case 'assigned':
      case 'buyer_assignment':
        return 'Approved'; // All post-approval stages show as "Approved"
      case 'pm_approval':
        return 'PM Approval';
      case 'third_approval':
        return 'Third Approval';
      case 'second_approval':
        return 'Second Approval';
      case 'first_approval':
        return 'First Approval';
      case 'declaration':
        return 'Awaiting Declaration';
      case 'evaluation':
        return 'Awaiting Evaluation';
      case 'quotation':
        return 'Awaiting Quotation';
      case 'draft':
        return 'Draft';
      default:
        return 'Pending';
    }
  };

  const handleFilterChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
    // Reset to first page when filters change
    setCurrentPage(1);
    // Filters will auto-apply via useEffect dependency
  };

  const clearFilters = () => {
    setFilters({
      status: 'pending', // Reset to default pending filter
      department: '',
      requester: '',
      search: '',
      supplier: '',
      glCode: '',
      capex: '',
      emergencyStatus: '',
      emergency: '',
      dateFrom: '',
      dateTo: '',
      dateRange: getCurrentMonthDates(), // Reset to current month, not empty
      amountMin: '',
      amountMax: '',
      amountRange: {
        min: '',
        max: ''
      }
    });
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      toast.info('Preparing export...');
      
      if (requisitions.length === 0) {
        toast.warning('No data to export');
        return;
      }
      
      // Prepare CSV data from currently filtered requisitions
      const headers = [
        'Requisition #',
        'Type',
        'Title',
        'Status',
        'Requester',
        'Division',
        'GL Code',
        'Capex',
        'Supplier',
        'Amount',
        'Date'
      ];
      
      const rows = requisitions.map(req => {
        const quotationTotal = req.quotation_total ? 
          parseFloat(req.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
        
        let displaySupplier = 'None';
        if (req.quotation_company_name && req.quotation_company_name !== 'Not Selected' && !req.quotation_company_name.startsWith('Supplier ID:')) {
          displaySupplier = req.quotation_company_name;
        } else if (req.company_name_actual && req.company_name_actual !== 'Not Selected' && !req.company_name_actual.startsWith('Supplier ID:')) {
          displaySupplier = req.company_name_actual;
        }
        
        return [
          `EPR-${req.requisition_number || req.requisition_id}`,
          req.specification_emergency == 1 ? 'ESR' : 'RFQ',
          req.requisition_title || req.specification_title || 'N/A',
          getStatusLabel(req),
          req.user_full_name || 'Unknown',
          req.department_name || 'N/A',
          req.gl_code || 'N/A',
          req.requisition_capex || '',
          displaySupplier,
          quotationTotal.toFixed(2),
          formatDate(req.requisition_date || req.created_at)
        ];
      });
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells that contain commas, quotes, or newlines
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const filename = `Requisitions_Report_${dateStr}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${requisitions.length} requisitions`);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (error) {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'junked': 'bg-gray-100 text-gray-800',
      'archived': 'bg-purple-100 text-purple-800',
      'purchase_order': 'bg-blue-100 text-blue-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'purchase_order':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'rejected':
      case 'junked':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'pending':
        return (
          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const getTotalValue = () => {
    return requisitions.reduce((total, req) => {
      const quotationTotal = req.quotation_total ? 
        parseFloat(req.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
      return total + quotationTotal;
    }, 0);
  };

  const getAverageValue = () => {
    if (requisitions.length === 0) return 0;
    return getTotalValue() / requisitions.length;
  };

  const getApprovedCount = () => {
    return requisitions.filter(req => getRequisitionStatus(req) === 'approved').length;
  };

  const getPendingCount = () => {
    return requisitions.filter(req => getRequisitionStatus(req) === 'pending').length;
  };

  const getEmergencyCount = () => {
    return requisitions.filter(req => req.specification_emergency == 1).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Requisitions Reports</h1>
                <p className="text-xs text-gray-500 mb-1">View requisition analytics and processing metrics</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {totalCount} requisition{totalCount !== 1 ? 's' : ''} total
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Reset all filters to default (Pending status, current month)"
                >
                  Reset Filters
                </button>
                <button
                  onClick={handleExport}
                  className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ 
                    backgroundColor: '#0087D4', 
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                  }}
                  title="Export filtered results to Excel"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Report Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="mb-3 flex items-center justify-between gap-4">
            <h2 className="text-lg font-medium text-gray-900">Report Filters</h2>
            <div className="flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search by specification title..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              />
            </div>
          </div>

          {/* First Filter Row - 5 columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            {/* Division filter - only show for HOD users */}
            {isHOD && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Division {departments.length > 0 && <span className="text-xs text-gray-500">({departments.length})</span>}
                </label>
                <SearchableSelect
                  options={departments}
                  value={filters.department}
                  onChange={(value) => handleFilterChange('department', value)}
                  disabled={departments.length === 0}
                  allText="All divisions in my department"
                  getOptionLabel={(dept) => dept.department_name}
                  getOptionValue={(dept) => dept.department_name}
                  className="w-full"
                />
              </div>
            )}

            {/* Show message for non-HOD users */}
            {!isHOD && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Division</label>
                <div className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-600">
                  Your division only
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                User {users.length > 0 && <span className="text-xs text-gray-500">({users.length})</span>}
              </label>
              <SearchableSelect
                options={users}
                value={filters.requester}
                onChange={(value) => handleFilterChange('requester', value)}
                disabled={users.length === 0}
                allText="All Users"
                getOptionLabel={(user) => user.user_full_name}
                getOptionValue={(user) => user.user_full_name}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Supplier {suppliers.length > 0 && <span className="text-xs text-gray-500">({suppliers.length})</span>}
              </label>
              <SearchableSelect
                options={suppliers.filter(supplier => supplier.BP_Code !== '11111111' && supplier.BP_Name?.trim() !== 'SILULUMANZI-DUMMY-SUPPLIER')}
                value={filters.supplier}
                onChange={(value) => handleFilterChange('supplier', value)}
                disabled={suppliers.length === 0}
                allText="All Suppliers"
                getOptionLabel={(supplier) => supplier.BP_Name}
                getOptionValue={(supplier) => supplier.BP_Name}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                GL Code {glCodes.length > 0 && <span className="text-xs text-gray-500">({glCodes.length})</span>}
              </label>
              <SearchableSelect
                options={glCodes}
                value={filters.glCode}
                onChange={(value) => handleFilterChange('glCode', value)}
                disabled={glCodes.length === 0}
                allText="All GL Codes"
                getOptionLabel={(code) => `${code.gl_code} - ${code.description || code.gl_name || ''}`}
                getOptionValue={(code) => code.gl_code}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Capex {capexValues.length > 0 && <span className="text-xs text-gray-500">({capexValues.length})</span>}
              </label>
              <SearchableSelect
                options={capexValues.map(capex => ({ value: capex, label: capex }))}
                value={filters.capex}
                onChange={(value) => handleFilterChange('capex', value)}
                disabled={capexValues.length === 0}
                allText="All Capex"
                getOptionLabel={(item) => item.label}
                getOptionValue={(item) => item.value}
                className="w-full"
              />
            </div>
          </div>

          {/* Second Filter Row - 6 columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">

                 <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Requisition Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="junked">Junked</option>
                <option value="archived">Archived</option> 
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.emergency}
                onChange={(e) => handleFilterChange('emergency', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="1">ESR</option>
                <option value="0">RFQ</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <CustomDatePicker
                value={filters.dateRange.start}
                onChange={(date) => handleFilterChange('dateRange.start', date)}
                placeholder="Start Date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
              <CustomDatePicker
                value={filters.dateRange.end}
                onChange={(date) => handleFilterChange('dateRange.end', date)}
                placeholder="End Date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Min Amount</label>
              <input
                type="number"
                placeholder="0.00"
                value={filters.amountRange.min}
                onChange={(e) => handleFilterChange('amountRange.min', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Max Amount</label>
              <input
                type="number"
                placeholder="0.00"
                value={filters.amountRange.max}
                onChange={(e) => handleFilterChange('amountRange.max', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

       
          </div>
        </div>

        {/* Requisitions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {requisitions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No requisitions found matching your criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requisition # / ESR
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requester / Division
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      GL Code / Capex
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requisitions.map((requisition, index) => (
                    <tr key={requisition.requisition_id || index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/dashboard/requisitions/${requisition.requisition_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="View requisition"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </Link>
                          <div>
                            <div>
                              EPR-{requisition.requisition_number || requisition.requisition_id}
                            </div>
                            {requisition.specification_emergency == 1 && (
                              <div className="mt-0.5">
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                  ESR
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 max-w-xs">
                        {(() => {
                          const fullTitle = requisition.requisition_title || requisition.specification_title || 'N/A';
                          const trimmedTitle = fullTitle.length > 15 ? fullTitle.substring(0, 15) + '...' : fullTitle;
                          
                          return (
                            <div className="truncate" title={fullTitle}>
                              {trimmedTitle}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${getStatusColor(getRequisitionStatus(requisition))}`}>
                          {getStatusLabel(requisition)}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{requisition.user_full_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500" title={requisition.department_name || 'N/A'}>
                            {(() => {
                              const deptName = requisition.department_name || 'N/A';
                              return deptName.length > 15 ? deptName.substring(0, 15) + '...' : deptName;
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div>{requisition.gl_code || 'N/A'}</div>
                          {requisition.requisition_capex && (
                            <div className="text-xs mt-0.5">
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                {requisition.requisition_capex}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          // Match the logic from RequisitionsList.jsx for supplier display
                          let displaySupplier = 'None';
                          if (requisition.quotation_company_name && requisition.quotation_company_name !== 'Not Selected' && !requisition.quotation_company_name.startsWith('Supplier ID:')) {
                            displaySupplier = requisition.quotation_company_name;
                          } else if (requisition.company_name_actual && requisition.company_name_actual !== 'Not Selected' && !requisition.company_name_actual.startsWith('Supplier ID:')) {
                            displaySupplier = requisition.company_name_actual;
                          }
                          
                          // Trim supplier name to prevent horizontal scrolling
                          const trimmedSupplier = displaySupplier.length > 15 ? displaySupplier.substring(0, 15) + '...' : displaySupplier;
                          
                          return (
                            <div title={displaySupplier} className="truncate max-w-xs">
                              {trimmedSupplier}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          // Use quotation_total like in RequisitionsList.jsx
                          const quotationTotal = requisition.quotation_total ? 
                            parseFloat(requisition.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
                          return formatCurrency(quotationTotal);
                        })()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(requisition.requisition_date || requisition.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequisitionsReports;